import { first_non_empty_number } from "./configure_signers_helpers";

function get_user_id(user) {
    return first_non_empty_number(user?.user_id);
}

function get_entity_profile_id(entity_profile) {
    return first_non_empty_number(entity_profile?.entity_profile_id);
}

function get_user_first_name(user) {
    return user?.first_name || "";
}

function get_user_last_name(user) {
    return user?.last_name || "";
}

function get_user_email(user) {
    return user?.email || "";
}

function get_entity_name(entity_profile) {
    return entity_profile?.entity_name || "";
}

function build_entity_profile_user(entity_profile, user) {
    const mapped_user = {
        user_id: get_user_id(user),
        first_name: get_user_first_name(user),
        last_name: get_user_last_name(user),
        email: get_user_email(user),
        entity_profile_id: get_entity_profile_id(entity_profile),
        entity_name: get_entity_name(entity_profile)
    };
    return mapped_user;
}

function get_entity_profile_users(entity_profile) {
    const users = Array.isArray(entity_profile?.users) ? entity_profile.users : [];
    const mapped_users = users.map(function map_user(user) {
        return build_entity_profile_user(entity_profile, user);
    });
    return mapped_users;
}

function get_results_from_root(parsed_response) {
    return Array.isArray(parsed_response?.results) ? parsed_response.results : [];
}

function get_results_from_data(parsed_response) {
    return Array.isArray(parsed_response?.data?.results) ? parsed_response.data.results : [];
}

function has_user_id(user) {
    return Boolean(user.user_id);
}

function is_unique_user_option(user, index, collection) {
    const duplicate_index = collection.findIndex(function find_duplicate_user(item) {
        return item.user_id === user.user_id;
    });
    return duplicate_index === index;
}

function normalize_entity_profiles(parsed_response) {
    const root_results = get_results_from_root(parsed_response);
    if (root_results.length) {
        return root_results;
    }

    const nested_results = get_results_from_data(parsed_response);
    return nested_results;
}

export function normalize_user_options(entity_profiles) {
    const normalized_options = entity_profiles
        .flatMap(function flatten_profile_users(entity_profile) {
            return get_entity_profile_users(entity_profile);
        })
        .filter(function filter_empty_users(user) {
            return has_user_id(user);
        })
        .filter(function filter_duplicate_users(user, index, collection) {
            return is_unique_user_option(user, index, collection);
        });
    return normalized_options;
}

function get_step_entity_type_id(step) {
    const entity_type_id = first_non_empty_number(step?.entity_type?.id, step?.entity_type_id, step?.entity_type);
    return entity_type_id;
}

function get_step_role_name(step, index) {
    const fallback_role_name = step?.name || step?.label || `Signer role ${index + 1}`;
    return fallback_role_name;
}

function get_step_name(step, fallback_role_name) {
    const entity_type_name = step?.entity_type?.entity_type_name || step?.entity_type_name || fallback_role_name;
    return entity_type_name;
}

function get_step_id(step) {
    return first_non_empty_number(step?.id);
}

function get_step_order(step, index) {
    return first_non_empty_number(step?.order) || index + 1;
}

function get_expected_signer_count(step) {
    return first_non_empty_number(step?.expected_signer_count) || 1;
}

export function normalize_workflow_step(step, index) {
    const entity_type_id = get_step_entity_type_id(step);
    const fallback_role_name = get_step_role_name(step, index);
    const workflow_step = {
        id: get_step_id(step),
        order: get_step_order(step, index),
        expected_signer_count: get_expected_signer_count(step),
        entity_type_id,
        entity_type_name: get_step_name(step, fallback_role_name)
    };
    return workflow_step;
}

export function is_valid_workflow_step(step) {
    return Boolean(step.id && step.entity_type_id);
}

export function sort_workflow_steps(first_step, second_step) {
    return first_step.order - second_step.order;
}

function get_signing_mode(signer_config) {
    return signer_config?.data?.signing_mode || "sequential";
}

function should_notify_in_order(signer_config) {
    return get_signing_mode(signer_config) !== "parallel";
}

function get_workflow_assignment_id(workflow_assignment) {
    return Number(workflow_assignment.id || 0) || null;
}

function get_workflow_id(workflow_assignment) {
    return Number(workflow_assignment.workflow || 0) || null;
}

function get_workflow_name(workflow_details) {
    return workflow_details?.data?.name || "";
}

function build_signer_slots(vm, workflow_steps, signer_config, assigned_signers_by_step, user_options_by_entity_type) {
    return vm.build_signer_slots(
        workflow_steps,
        signer_config?.data,
        assigned_signers_by_step,
        user_options_by_entity_type
    );
}

export function apply_loaded_signer_configuration(
    vm,
    signer_config,
    workflow_assignment,
    workflow_details,
    workflow_steps,
    assigned_signers_by_step,
    user_options_by_entity_type
) {
    vm.signer_configuration = signer_config?.data || null;
    vm.workflow_assignment_id = get_workflow_assignment_id(workflow_assignment);
    vm.workflow_id = get_workflow_id(workflow_assignment);
    vm.workflow_name = get_workflow_name(workflow_details);
    vm.workflow_steps = workflow_steps;
    vm.notify_in_order = should_notify_in_order(signer_config);
    vm.signer_slots = build_signer_slots(
        vm,
        workflow_steps,
        signer_config,
        assigned_signers_by_step,
        user_options_by_entity_type
    );
    vm.sync_slot_source_types();
}

export function normalize_entity_profile_results(parsed_response) {
    const entity_profiles = normalize_entity_profiles(parsed_response);
    return entity_profiles;
}
